import React from 'react'
import {cx} from '@emotion/css'

import {
  BottomContent,
  CenterContent,
  EndContent,
  NavBarElement,
  NavBarVariant,
  StartContent
} from './styles'

export interface NavBarProps {
  /** Fixed-width content rendered at the start (e.g. logo or back button). */
  startContent?: React.ReactNode
  /** Responsive content rendered between the start and end content (e.g. title or search box). */
  centerContent?: React.ReactNode
  /** Fixed-width content rendered at the end (e.g. user widget or settings). */
  endContent?: React.ReactNode
  /** Responsive content rendered full-width on its own row below the primary content. */
  bottomContent?: React.ReactNode
  /**
   * When true the center content becomes full-width and wraps onto a second line,
   * leaving the start and end content inline on the first line. Used to replicate
   * the responsive SRP header where the search box drops below the logo and user
   * widget. When false (default) the center content stays inline and responsive.
   */
  wrapCenterContent?: boolean
  /**
   * Centers the center content relative to the whole bar by reserving symmetric
   * space on both sides — even when a side is empty (e.g. a title with only a
   * close button). Use for title-style bars; leave off (default) when the center
   * content should fill the available space (e.g. a search box).
   */
  centerAligned?: boolean
  /**
   * Visual variant controlling the background and content colours:
   * `default` (neutral background), `transparent`, or `inverse` (brand inverse
   * background with inverse content).
   */
  variant?: NavBarVariant
  /** Whether to display a divider flush to the bottom of the container. */
  withDivider?: boolean
  /**
   * Positions the bar absolutely (top/left/right: 0) so it overlaps the content
   * below it. The nearest positioned ancestor becomes its containing block.
   */
  floating?: boolean
  /**
   * Forces the compact (mobile) height and paddings at every breakpoint instead
   * of growing to the desktop metrics. Use where the bar must stay slim on
   * desktop (e.g. embedded or overlay contexts).
   */
  isCompact?: boolean
  /**
   * Makes the primary row a fixed, animatable height instead of
   * `minmax(min, auto)`. Combined with a `transition`, this lets a consumer
   * collapse the row (e.g. on scroll) by overriding
   * `--nav-bar-primary-row-min-height` to 0. Trades away the content-driven
   * grow, so only use it where the primary row's height is known to be fixed.
   */
  collapsiblePrimaryRow?: boolean
  /** Pass-through className to allow style overrides. */
  className?: string
}

export const NavBar = ({
  startContent,
  centerContent,
  endContent,
  bottomContent,
  wrapCenterContent = false,
  centerAligned = false,
  variant = 'default',
  withDivider = false,
  floating = false,
  isCompact = false,
  collapsiblePrimaryRow = false,
  className
}: NavBarProps) => {
  return (
    <NavBarElement
      className={cx(
        floating && '--is-floating',
        isCompact && '--is-compact',
        className
      )}
      wrapCenterContent={wrapCenterContent}
      centerAligned={centerAligned}
      variant={variant}
      withDivider={withDivider}
      hasCenterContent={Boolean(centerContent)}
      hasBottomContent={Boolean(bottomContent)}
      hasPrimaryRowContent={Boolean(startContent || endContent)}
      collapsiblePrimaryRow={collapsiblePrimaryRow}
    >
      {startContent && <StartContent>{startContent}</StartContent>}
      {centerContent && <CenterContent>{centerContent}</CenterContent>}
      {endContent && <EndContent>{endContent}</EndContent>}
      {bottomContent && (
        <BottomContent wrapCenterContent={wrapCenterContent}>
          {bottomContent}
        </BottomContent>
      )}
    </NavBarElement>
  )
}
